import {
    Children,
    cloneElement,
    createContext,
    type FC,
    type KeyboardEvent as ReactKeyboardEvent,
    type PropsWithChildren,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useId,
    useRef,
    useState
} from "react";

import {
    type IntersectionOptions,
    useInView
} from "react-intersection-observer";

import {
    deepForEach,
    deepMap,
    isTab,
    isTabList,
    isTabPanel
} from "./tab-helper";

export type Adg4TabsContextType = {
    currentTab: number;
    /**
     * User has selected a tab
     */
    userSelected: boolean;
    isCurrentTab: (tabIndex?: number) => boolean;
    goToTab: (tabIndex?: number, clearTimer?: boolean) => void;
    goToNextTab: () => void;
    goToPrevTab: () => void;
    goToFirstTab: () => void;
    goToLastTab: () => void;
    onKeyDown: (e: ReactKeyboardEvent) => void;
};

const Adg4TabsContext = createContext<Adg4TabsContextType | null>(null);

export const useAdg4TabsContext = () => {
    const context = useContext(Adg4TabsContext);
    if (!context) {
        throw new Error("useAdg4TabsContext must be used within Adg4Tabs");
    }
    return context;
};

type Adg4TabsProps = PropsWithChildren<{
    initialTab?: number;
    loop?: boolean;
    tabSwitchInterval?: number;
    /**
     * Custom options for the useInView hook
     */
    useInViewOptions?: IntersectionOptions;
}>;

export const Adg4Tabs: FC<Adg4TabsProps> = ({
    initialTab,
    loop = true,
    children,
    tabSwitchInterval,
    useInViewOptions
}: Adg4TabsProps) => {
    const [currentTab, setCurrentTab] = useState<number>(initialTab ?? 0);
    const [userSelected, setUserSelected] = useState<boolean>(false);

    const [focus, setFocus] = useState<boolean>(false);
    const tabIds = useRef<string[]>([]);
    const contextId = useId();
    const timerRef = useRef<NodeJS.Timeout>();
    const DEFAULT_IN_VIEW_THRESHOLD = 0.3;

    const { ref: scrollRef, inView: sectionIsVisible } = useInView({
        threshold: DEFAULT_IN_VIEW_THRESHOLD,
        ...useInViewOptions
    });

    function getTabsCount() {
        let count = 0;
        deepForEach(children, (child) => {
            if (isTab(child)) {
                count++;
            }
        });
        return count;
    }

    function getChildren() {
        let panelIndex = 0;
        tabIds.current = tabIds.current || [];
        let diff = tabIds.current.length - getTabsCount();

        // Add ids if new tabs have been added
        // Don't bother removing ids, just keep them in case they are added again
        // This is more efficient, and keeps the uuid counter under control
        while (diff++ < 0) {
            tabIds.current.push(`${contextId}${tabIds.current.length}`);
        }
        // Map children to dynamically setup refs
        return deepMap(children, (child) => {
            let result = child;

            // Check if the window is available
            const windowAvailable =
                !!window &&
                !!window.document &&
                !!window.document.activeElement;
            // Get the active element id
            const activeElementId = windowAvailable
                ? window.document.activeElement?.id
                : null;

            // Clone TabList and Tab components to have refs
            if (isTabList(child)) {
                let listIndex = 0;

                // Figure out if the current focus in the DOM is set on a Tab
                // If it is we should keep the focus on the next selected tab
                let wasTabFocus = false;
                if (windowAvailable && activeElementId) {
                    // Check if the active element is a tab
                    wasTabFocus = Children.toArray(
                        (child as ReactElement).props.children
                    )
                        .filter(isTab)
                        .some((_, i) => {
                            return (
                                activeElementId === `tab${tabIds.current[i]}`
                            );
                        });
                }

                result = cloneElement(child as ReactElement, {
                    children: deepMap(
                        (child as ReactElement).props.children,
                        (tab) => {
                            const selected = currentTab === listIndex;

                            const props = {
                                id: tabIds.current[listIndex],
                                tabIndex: listIndex,
                                focus: selected && (focus || wasTabFocus)
                            };

                            listIndex++;
                            return cloneElement(tab as ReactElement, props);
                        }
                    )
                });
            } else if (isTabPanel(child)) {
                const props = {
                    id: tabIds.current[panelIndex],
                    panelIndex
                };

                panelIndex++;

                result = cloneElement(child as ReactElement, props);
            }

            return result;
        });
    }

    const goToTab = (tabIndex?: number, clearTimer?: boolean) => {
        if (tabIndex === undefined) {
            return;
        }
        setCurrentTab(tabIndex);
        if (clearTimer) {
            setUserSelected(true);
            clearInterval(timerRef.current);
        }
    };

    const isCurrentTab = (tabIndex?: number) => {
        return tabIndex === currentTab;
    };

    const goToNextTab = useCallback(() => {
        if (currentTab + 1 < tabIds.current.length) {
            goToTab(currentTab + 1);
        } else if (loop) {
            goToTab(0);
        }
    }, [loop, currentTab]);

    const goToPrevTab = useCallback(() => {
        if (currentTab - 1 >= 0) {
            goToTab(currentTab - 1);
        } else if (loop) {
            goToTab(tabIds.current.length - 1);
        }
    }, [loop, currentTab]);

    const goToFirstTab = () => {
        goToTab(0);
    };

    const goToLastTab = () => {
        goToTab(tabIds.current.length - 1);
    };

    const onKeyDown = (e: ReactKeyboardEvent) => {
        let preventDefault = false;
        if (e.code === "ArrowLeft" || e.code === "ArrowUp") {
            goToPrevTab();
            preventDefault = true;
        } else if (e.code === "ArrowRight" || e.code === "ArrowDown") {
            goToNextTab();
            preventDefault = true;
        } else if (e.code === "End") {
            // Select last tab (End key)
            goToFirstTab();
            preventDefault = true;
        } else if (e.code === "Home") {
            // Select first tab (Home key)
            goToLastTab();
            preventDefault = true;
        }

        setFocus(true);

        // This prevents scrollbars from moving around
        if (preventDefault) {
            e.preventDefault();
        }
    };

    // Reset the userSelected state when the section is visible again
    useEffect(() => {
        if (sectionIsVisible) {
            setUserSelected(false);
        }
    }, [sectionIsVisible]);

    // Auto switch tabs effect
    useEffect(() => {
        // clear out any timer that exists
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        if (tabSwitchInterval && sectionIsVisible && !userSelected) {
            timerRef.current = setTimeout(goToNextTab, tabSwitchInterval);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [
        currentTab,
        tabSwitchInterval,
        sectionIsVisible,
        goToNextTab,
        userSelected
    ]);

    return (
        <Adg4TabsContext.Provider
            value={{
                currentTab,
                userSelected,
                goToTab,
                isCurrentTab,
                goToNextTab,
                goToPrevTab,
                goToFirstTab,
                goToLastTab,
                onKeyDown
            }}
        >
            <div ref={scrollRef}>{getChildren()}</div>
        </Adg4TabsContext.Provider>
    );
};
